import {
  ActionsGroup as ActionsGroupUI,
  Blank,
  Dialog as UIDialog,
  DialogActions as UIDialogActions,
  DialogContent as UIDialogContent,
  DialogTitle as UIDialogTitle,
  FlatButton,
  Icon as IconUI,
  Image as ImageUI,
  ImageLoader as SImageLoader,
  InputLabel,
  Textarea as TextareaUI,
  transition,
  WaitingButton,
} from "@evergis/ui";
import { Flex } from "grid-styled";
import styled, { css } from "styled-components";

export const SourceDescription = styled.span<{ error?: boolean; noPadding?: boolean }>`
  padding-left: ${({ noPadding }) => (noPadding ? 0 : "0.5rem")};
  color: ${({ theme: { palette }, error }) => (error ? palette.error : palette.textPrimary)};
`;

export const SourceDescriptionName = styled.span<{ error?: boolean }>`
  color: ${({ theme: { palette }, error }) => (error ? palette.error : palette.textPrimary)};
`;

export const commonWidth = 52;

export const RestrictedWidthContainer = styled.span`
  width: ${commonWidth}rem;
  flex-direction: column;
  display: flex;
  flex-shrink: 0;
  align-items: normal;
`;

export const Dialog = styled(UIDialog)`
  align-items: center;
`;

export const DialogTitle = styled(UIDialogTitle)`
  padding-top: 4rem;
  padding-left: 0;
  padding-right: 0;
  width: ${commonWidth}rem;

  ${ActionsGroupUI}:last-of-type {
    margin-left: 0;
  }
`;

export const DialogContent = styled(UIDialogContent)`
  width: 100%;
  flex-wrap: nowrap;
  flex-grow: 0;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 0 0;
  overflow-y: scroll;
`;

export const DialogActions = styled(UIDialogActions)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
  padding: 0;
  transition: box-shadow ${props => (props.showShadow ? transition.hide : transition.show)};
`;

export const DialogActionsContentContainer = styled(RestrictedWidthContainer)`
  padding: 2.5rem 0;
  box-sizing: border-box;
`;

export const ImageLoaderContainer = styled(Flex)`
  width: 18.75rem;
  height: 15.125rem;
  flex-shrink: 0;
  margin-right: 1.5625rem;
  flex-direction: column;
`;

export const ImageLoader = styled(SImageLoader)`
  width: 100%;
  height: 100%;
  min-height: 1px;
`;

export const Image = styled(ImageUI)`
  width: 18.75rem;
  height: 15.125rem;
  flex-shrink: 0;
  margin-right: 1.5625rem;
`;

type ProjectMetaBlockProps = {
  reverseRow?: boolean;
};

export const ProjectMetaBlock = styled.div<ProjectMetaBlockProps>`
  display: flex;
  flex-direction: ${({ reverseRow }) => (reverseRow ? "row-reverse" : "row")};
  min-height: 15.125rem;
  width: 100%;

  ${({ reverseRow }) =>
    reverseRow
      ? css`
          ${Image} {
            margin-left: 1.5625rem;
            margin-right: 0;
          }

          ${ImageLoaderContainer} {
            margin-left: 1.5625rem;
            margin-right: 0;
          }
        `
      : ""}
`;

export const Icon = styled(IconUI)`
  position: absolute;
  left: -2rem;
`;

export const BlockTitle = styled.span`
  display: flex;
  align-items: center;
  margin: 2.5rem 0 1rem;
`;

export const AttributesBlockContainer = styled.span<{ noPaddingBottom?: boolean }>`
  display: flex;
  background-color: rgba(0, 0, 0, 0.04);
  padding-bottom: ${({ noPaddingBottom }) => (noPaddingBottom ? "0" : "2rem")};
  margin-top: 2.5rem;
  width: 100%;
  justify-content: center;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  position: relative;

  ${BlockTitle} {
    margin: 1rem 0 0.25rem;
  }
`;

export const AttributesHeader = styled.div`
  position: relative;
  left: -1.8125rem;
  display: flex;
  flex-direction: column;
  width: ${commonWidth}rem;
`;

export const TitleIcon = styled(IconUI)`
  margin-right: 0.75rem;
`;

export const Description = styled.div`
  font: ${({ theme: { fonts } }) => fonts.small};
  color: ${({ theme: { palette } }) => palette.textSecondary};

  ${AttributesBlockContainer} & {
    margin-left: 1.8125rem;
  }
`;

export const AttributesList = styled.div`
  margin-top: 1.5rem;
`;

export const AttributeBlank = styled(Blank)`
  display: flex;
  padding: 0.75rem 1.5rem 0.75rem 1.5rem;
  align-items: center;

  &:not(:last-of-type) {
    margin-bottom: 0.5rem;
  }

  > div:not(:last-of-type) {
    margin-right: 0.875rem;
  }
`;

export const SysAttributesListTitle = styled.div`
  font: ${({ theme: { fonts } }) => fonts.small};
  color: ${({ theme: { palette } }) => palette.textSecondary};
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

export const ApplyButton = styled(WaitingButton)`
  text-align: center;
  flex-shrink: 0;
`;
export const CancelButton = styled(FlatButton)`
  text-align: center;
  flex-shrink: 0;
`;
export const BackButton = styled(FlatButton)`
  flex-shrink: 0;
  text-align: center;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const FieldsContainer = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;

  > div:nth-of-type(2) {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  > *:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

export const Textarea = styled(TextareaUI)`
  flex-grow: 1;
  height: auto;
`;

export const Label = styled(InputLabel)`
  margin-bottom: 0.25rem;
`;

export const AliasBlock = styled.div`
  margin-bottom: 0.25rem;
`;

export const TitleContainer = styled.div<{ displayShadow?: boolean }>`
  width: 100%;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  transition: box-shadow ${props => (props.displayShadow ? transition.hide : transition.show)};
  box-shadow: ${props => props.displayShadow && "0 0.1875rem 0.625rem 0 rgba(0, 0, 0, 0.15)"};
  padding-bottom: 2rem;
  z-index: 1;
`;

export const CommonTitleContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DialogTitleContainer = styled(Flex)`
  align-items: center;
  margin-bottom: 0.25rem;
`;

export const LinkButton = styled.button`
  color: ${({ theme }) => theme.palette.primary} !important;
  font-size: 14px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
`;

export const StyledDialog = styled(Dialog)`
  border-radius: 12px;
  position: relative;
`;
